import React, { useCallback, useImperativeHandle, useRef, useState, useEffect } from "react";
import { isEqual, isFunction } from "@util/utils";
import InvoiceSettingDialog from "./InvoiceSettingDialog";
import ProductSettingDialog from "./ProductSettingDialog";
import CheckoutCompleteDialog from "./CheckoutCompleteDialog";
import ProductAddedConfirmDialog from "./ProductAddedConfirmDialog";

const INVOICE_SETTING_DIALOG = "InvoiceSettingDialog";
const PRODUCT_SETTING_DIALOG = "ProductSettingDialog";
const CHECKOUT_COMPLETE_DIALOG = "CheckoutCompleteDialog";
const PRODUCT_ADDED_CONFIRM_DIALOG = "ProductAddedConfirmDialog";

const PosDialogBox = React.forwardRef(({ onDialogStateChange }, ref) => {
	const [openDialog, setOpenDialog] = useState("");
	const [sourceData, setSourceData] = useState(null);
	const refreshRef = useRef(null);
	const prevDialogRef = useRef(""); // 追蹤前一個彈窗名字

	const handleDialogClose = useCallback(
		() => {
			setOpenDialog(null);
			setSourceData(null);
			refreshRef.current = null;
		},
		// eslint-disable-next-line
		[],
	);

	const doRefresh = (...arg) => {
		if (isFunction(refreshRef.current)) {
			const refreshFn = refreshRef.current;
			setTimeout(() => refreshFn(...arg), 5);
		}
	};

	// 監聽 openDialog 變化，自動觸發 onDialogStateChange
	useEffect(() => {
		if (!isFunction(onDialogStateChange)) return;

		const currentDialog = openDialog || "";
		const previousDialog = prevDialogRef.current;

		// 彈窗狀態變化
		if (currentDialog !== previousDialog) {
			// 關閉舊彈窗
			if (previousDialog) {
				onDialogStateChange(previousDialog, false);
			}
			// 打開新彈窗
			if (currentDialog) {
				onDialogStateChange(currentDialog, true);
			}
		}

		// 記前一個彈窗名字
		prevDialogRef.current = currentDialog;
		
		// eslint-disable-next-line
	}, [openDialog, onDialogStateChange]);

	useImperativeHandle(
		ref,
		() => ({
			// 開啟發票設定彈窗
			openInvoiceSettingDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(INVOICE_SETTING_DIALOG);
			},
			// 開啟商品設定彈窗
			openProductSettingDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(PRODUCT_SETTING_DIALOG);
			},
			// 開啟結帳完成彈窗
			openCheckoutCompleteDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(CHECKOUT_COMPLETE_DIALOG);
			},
			// 開啟商品加入確認彈窗
			openProductAddedConfirmDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData);
				setOpenDialog(PRODUCT_ADDED_CONFIRM_DIALOG);
			},
			// 關閉彈窗
			closeDialog: () => {
				handleDialogClose();
			},
		}),
		// eslint-disable-next-line
		[handleDialogClose],
	);

	return (
		<React.Fragment>
			<InvoiceSettingDialog open={isEqual(openDialog, INVOICE_SETTING_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<ProductSettingDialog open={isEqual(openDialog, PRODUCT_SETTING_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
			<CheckoutCompleteDialog open={isEqual(openDialog, CHECKOUT_COMPLETE_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} />
			<ProductAddedConfirmDialog open={isEqual(openDialog, PRODUCT_ADDED_CONFIRM_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} sourceData={sourceData} />
		</React.Fragment>
	);
});

export default PosDialogBox;
